import { Injectable } from '@angular/core';
import {
  Auth,
  signInWithEmailAndPassword,
  User,
  user as user$,
} from '@angular/fire/auth';
import { from, map, Observable } from 'rxjs';
import { KrulesService } from '../krules/krules.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  user?: User;
  fbAuth: Auth;
  _currentProject: string = '';
  constructor(private auth: Auth, private _krules: KrulesService) {
    this.fbAuth = auth;
  }

  getFireToken() {
    return user$(this.fbAuth).pipe(
      map((user: any) => {
        return (user as any).accessToken;
      })
    );
  }

  setCurrentProject(project: string) {
    this._currentProject = project;
    localStorage.setItem('current_project', project);
    window.location.reload();
  }

  getCurrentProject() {
    return this._currentProject;
  }

  init() {
    const lsUser = localStorage.getItem('user');
    this._currentProject = localStorage.getItem('current_project') || '';
    if (lsUser) {
      this.user = JSON.parse(lsUser);
    }
  }

  /**
   * Force refresh of the Firebase ID token, so any custom-claim changes
   * applied server-side (e.g. via the krules-companion-client CLI) take
   * effect without waiting for the SDK's automatic 1h refresh. Used by the
   * "Refresh permissions" UI in claim-based mode.
   */
  async refreshToken(): Promise<void> {
    const user = this.fbAuth.currentUser;
    if (user) {
      await user.getIdToken(true);
    }
  }

  login(email: string, password: string): Observable<any> {
    return from(
      signInWithEmailAndPassword(this.auth, email, password).then(async (data) => {
        this.user = data.user;
        localStorage.setItem('user', JSON.stringify({ ...data.user }));

        // Single source of truth for "what subscriptions can this user see":
        // the backend, which is mode + admin aware. Frontend treats the
        // response uniformly. See backend_api_v2/auth.py:
        // create_subscription_lister.
        const idToken = await data.user.getIdToken();
        const subscriptions = await this._krules.getSubscriptions(idToken);

        if (subscriptions.length === 0) {
          // No accessible subscriptions: leave the current project empty.
          // Downstream views will render empty UI rather than crashing.
          this.setCurrentProject('');
          return;
        }

        // Preserve the previously selected project across logins when it's
        // still in the visible list; otherwise pick the first available.
        const stored = localStorage.getItem('current_project') || '';
        const target = subscriptions.includes(stored) ? stored : subscriptions[0];
        this.setCurrentProject(target);
      })
    );
  }

  logout(): Observable<any> {
    return from(
      this.auth.signOut().then(() => {
        localStorage.removeItem('user');
      })
    );
  }
}
